"use client";

import { useCallback, useEffect, useState } from "react";
import Image from "next/image";
import { Scheme } from "@/type/schemeType";
import icon from "../icons/Icons";
import { AnimatePresence, easeInOut, motion } from "framer-motion";
import { useIsMobile } from "@/utils/useIsMobile";
import { usePathname } from "next/navigation";
import { useLoginModal } from "@/utils/useLoginModal";
import Link from "next/link";
import successAnimation from "../../../public/next_assets/lottie/success-tick.json";
import Lottie from "lottie-react";
import { increaseCart } from "@/redux/features/AddToCart/cartSlice";
import CustomModal from "./CustomModal";
import { apiPost } from "@/utils/apiClient";
import Icon from "../icons/Icons";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@/redux/store";
import { closeInvestmentModal } from "@/redux/features/InvestmentModal/investmentModalSlice";
import { formatIndianNumber } from "@/utils/commonFunctions";
import { SchemeOptional } from "@/type/commonTypes";
import CustomDropdown from "../ui/CustomDropdown";
import { useRef } from "react";
import { ROOT_URL } from "@/utils/gloableVariables";

export default function InvestmentModal() {
  const dispatch = useDispatch();
  const [isDropdownOpenPeriod, setIsDropdownOpenPeriod] = useState(false);
  const [stepUpError, setStepUpError] = useState("");
  const STEP_UP_MIN = 1000;
  const STEP_UP_MAX = 1_000_000; // ₹10,00,000
  const periodButtonRef = useRef<HTMLButtonElement>(null);
  const isOpen = useSelector(
    (state: RootState) => state.investmentModal.isOpen,
  );
  const schemeData: SchemeOptional | Scheme | null = useSelector(
    (state: RootState) => state.investmentModal.schemeData,
  );

  const pathName = usePathname();
  const PERIODS = [
    { label: "3 Months", value: 3 },
    { label: "6 Months", value: 6 },
    { label: "1 Year", value: 12 },
    { label: "3 Years", value: 36 },
    { label: "5 Years", value: 60 },
  ] as const;

  type PeriodOption = (typeof PERIODS)[number];
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodOption>(
    PERIODS[0],
  );
  const { open, isLoggedIn, setModalKey } = useLoginModal();

  const getInitialType = () => {
    if (schemeData?.sip_allowed) return "SIP";
    if (schemeData?.lump_allowed) return "Lumpsum";
    return "SIP";
  };

  const [investmentType, setInvestmentType] = useState<
    "SIP" | "Lumpsum" | "Stepup"
  >(getInitialType());
  const [amount, setAmount] = useState("1000");
  const [sipDate, setSipDate] = useState<number>(1);
  const [showError, setShowError] = useState(false);
  const [setError, setSetError] = useState("");
  const [addError, setAddError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [addSuccess, setAddSuccess] = useState(false);
  const [fundSuspended, setFundSuspended] = useState(false);
  const [isStepUp, setIsStepUp] = useState(false);
  const [stepUpPercent, setStepUpPercent] = useState("1000");

  const isMobile = useIsMobile(631);
  const MAX_INVESTMENT_AMOUNT = investmentType === "SIP" ? 1000000 : 10000000;

  const getDefaultAmount = (type: "SIP" | "Lumpsum") => {
    const val = formatIndianNumber(
      type === "SIP"
        ? (schemeData?.minimum_sip_invest ?? 1000)
        : (schemeData?.minimum_investment ?? 1000),
    );

    setAmount(val);
    return val;
  };

  useEffect(() => {
    if (!schemeData?.sip_allowed && !schemeData?.lump_allowed) {
      setFundSuspended(true);
    } else {
      setFundSuspended(false);
    }

    if (!isStepUp || investmentType == "Lumpsum") {
      setStepUpPercent("1000");
      setSelectedPeriod(PERIODS[0]);
      setStepUpError("");
      setIsDropdownOpenPeriod(false);
    }
  }, [schemeData, isStepUp, investmentType]);

  useEffect(() => {
    if (isOpen) {
      if (!isLoggedIn) {
        dispatch(closeInvestmentModal());
        open();
        setModalKey(`InvestMent Popup - ${pathName}`);
      }
      getDefaultAmount(getInitialType());
      setInvestmentType(getInitialType());
    }
  }, [isOpen, isLoggedIn]);

  const formatINR = (value: string) => {
    if (!value) return "";
    const num = Number(value);
    if (isNaN(num)) return "";
    return num.toLocaleString("en-IN");
  };

  const closeModal = useCallback(() => {
    setIsStepUp(false);
    setAddSuccess(false);
    dispatch(closeInvestmentModal());
  }, []);

  const validateAmount = (raw: string | number) => {
    const num =
      typeof raw === "string" ? Number(raw.replace(/[^0-9]/g, "") || "0") : raw;
    const formatted = num.toLocaleString("en-IN");
    setAmount(formatted);

    const minAmount =
      investmentType === "SIP"
        ? schemeData?.minimum_sip_invest || 1000
        : schemeData?.minimum_investment || 1000;

    if (num < minAmount) {
      setShowError(true);
      setSetError(
        `Minimum investment amount is ₹${minAmount.toLocaleString()}.`,
      );
      return false;
    } else if (num % 100 !== 0) {
      setShowError(true);
      setSetError("Amount should be divisible by ₹100.");
      return false;
    } else if (num > MAX_INVESTMENT_AMOUNT) {
      setShowError(true);
      setSetError(
        `Maximum investment amount is under ₹${MAX_INVESTMENT_AMOUNT}`,
      );
      return false;
    } else {
      setShowError(false);
      setSetError("");
      return true;
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    validateAmount(raw);
  };

  const handleQuickAmount = (
    e: React.MouseEvent<HTMLButtonElement>,
    val: number,
  ) => {
    e.preventDefault();
    const current = Number(amount.replace(/[^0-9]/g, "") || "0");
    validateAmount(current + val);
  };

  const handleAddToCart = async () => {
    const selectedAmount = parseInt(amount.replace(/[^0-9]/g, ""), 10);
    if (isStepUp) {
      const stepUpAmount = Number(stepUpPercent);

      if (
        !stepUpAmount ||
        stepUpAmount < STEP_UP_MIN ||
        stepUpAmount > STEP_UP_MAX
      ) {
        setStepUpError(
          stepUpAmount < STEP_UP_MIN
            ? "Step-up amount must be at least ₹1000"
            : "Step-up amount should not be greater than ₹1000000",
        );
        return;
      }
    }
    const isValid = validateAmount(selectedAmount);
    if (!isValid) return;

    setIsLoading(true);

    const selectedInvestmentType = investmentType;
    const sipDateFormatted = sipDate.toString();

    if (schemeData) {
      const payload: Record<string, string> = {
        scheme_code: schemeData.scheme_code || "",
        inv_type: selectedInvestmentType.toLowerCase(),
        amount: selectedAmount.toString(),
        sip_date: sipDateFormatted || "1",
        offset: "",
        addnl_investment: "",
        folio_no: "",
        source: pathName,
        medium: "Web",
        folio: "",
      };

      if (isStepUp) {
        payload.stepup_amount = stepUpPercent;
        payload.stepup_frequency = selectedPeriod.value.toString();
        payload.inv_type = "stepup";
      }

      try {
        const res = await apiPost("addToCart", payload);

        if (res.status) {
          setIsLoading(false);
          dispatch(increaseCart());
          setAddSuccess(true);
        } else {
          setIsLoading(false);
          console.error("Something went wrong");
        }
      } catch (err) {
        setIsLoading(false);
        setAddError(`${err} Error while adding to cart.`);
        console.error(err);
      }
    }

    setIsLoading(false);
  };

  const handleSwtiching = (type: string) => {
    const isAllowed =
      type === "SIP" ? schemeData?.sip_allowed : schemeData?.lump_allowed;

    if (isAllowed) {
      setInvestmentType(type as "SIP" | "Lumpsum");
      setShowError(false);
      setAmount(getDefaultAmount(type as "SIP" | "Lumpsum"));
    }
  };

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        onClose={closeModal}
        mobileCondition={631}
        maxSize="max-w-500"
        className="bg-white rounded-t-5xl xs:rounded-xl overflow-hidden"
        showCloseButton={isMobile ? true : false}
      >
        {!addSuccess ? (
          <>
            <div
              className={`sticky top-0 xs:static z-1 flex items-center justify-start py-3 xs:py-3.5 px-4 gap-2.5 shadow-[0px_0px_10px_0px_rgba(10,13,18,0.25)] xs:shadow-none ${fundSuspended ? "bg-[#ec4d4d]" : "bg-[#e1e8ff]"}`}
            >
              <Image
                src={`${ROOT_URL}img/AMCsLogo/${schemeData?.amc_code}.png`}
                alt={schemeData?.scheme_name || "Fund Icon"}
                width={32}
                height={32}
                className="w-6 h-6 xs:w-8 xs:h-8 bg-white shrink-0 p-1 overflow-hidden border border-[#1a51aa66] rounded-full"
              />

              <p
                className={`text-sm xs:text-base font-medium line-clamp-2 ${fundSuspended ? "text-white" : "text-primaryBlack"}`}
              >
                {schemeData?.scheme_name ||
                  "Axis Gold Fund - Regular Plan - Growth Option"}
              </p>

              {isMobile ? null : (
                <button
                  onClick={closeModal}
                  className={`w-6 h-6 transition-all duration-300 hover:scale-[1.03] ${fundSuspended ? "text-white" : "text-primaryBlack"} ml-auto shrink-0`}
                  aria-label="Close Button"
                >
                  <icon.popupCloseIcon />
                </button>
              )}
            </div>

            {fundSuspended ? (
              <>
                {/* Suspended Funds */}
                <div className="px-4 py-3 xs:p-5 bg-white">
                  <p className="font-medium text-[#ec4d4d] text-sm xs:text-base text-center">
                    <span className="font-bold">Fund Suspended:</span> Currently
                    this fund is not available for Investments.
                  </p>
                </div>
              </>
            ) : (
              <>
                {/* Investment Type Buttons */}
                <div className="p-5">
                  <div className="mb-4">
                    <p className="text-sm xs:text-base mb-1.5 text-primaryBlack">
                      Select Investment Type
                    </p>
                    <div className="flex flex-col xs:flex-row gap-2 xs:gap-5">
                      {["SIP", "Lumpsum"].map((type) => {
                        const isAllowed =
                          type === "SIP"
                            ? schemeData?.sip_allowed
                            : schemeData?.lump_allowed;

                        return (
                          <label
                            key={type}
                            className={`group w-full flex items-center justify-between py-2.5 px-4 rounded-xl border ${investmentType === type ? "border-primaryBlue bg-[#f4f8ff]" : "border-[#e4e4e4] hover:shadow-[0px_1px_8px_0px_rgba(10,13,18,0.15)] hover:bg-[#f4f8ff]"} ${!isAllowed ? "opacity-50 cursor-not-allowed hover:shadow-none hover:bg-white" : "cursor-pointer"} flex-1 transition-all duration-350 ease-in-out`}
                          >
                            <input
                              type="radio"
                              name="investmentType"
                              checked={investmentType === type}
                              disabled={!isAllowed}
                              onChange={() => handleSwtiching(type)}
                              className="hidden"
                            />
                            <p
                              className={`text-left font-medium text-2xs xs:text-sm ${investmentType === type ? "text-primaryBlue" : "text-[#414651]"}`}
                            >
                              {type === "SIP"
                                ? "SIP (Monthly)"
                                : "Lumpsum (One Time)"}
                            </p>
                            <i
                              className={`inline-flex items-center justify-center w-4 h-4 rounded-full bg-white border-2 ${investmentType === type ? 'border-primaryBlue after:content-[""] after:w-2 after:h-2 after:rounded-full after:bg-primaryBlue' : "border-[#e4e4e4]"}`}
                            />
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {investmentType === "SIP" && (
                    <>
                      <div
                        className={`mt-4  flex items-center mb-2  ${isStepUp ? "mb-2" : "mb-2"}`}
                      >
                        <p
                          className={`pr-2 text-sm xs:text-base ${isStepUp ? "text-black" : "text-gray-400"}`}
                        >
                          Step-up (SIP)
                        </p>
                        <button
                          type="button"
                          onClick={() => setIsStepUp(!isStepUp)}
                          aria-pressed={isStepUp}
                          className={[
                            "relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-300",
                            isStepUp ? "bg-green-500" : "bg-gray-300",
                          ].join(" ")}
                        >
                          <span
                            className={[
                              "inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300",
                              isStepUp ? "translate-x-4.5" : "translate-x-0.5",
                            ].join(" ")}
                          />
                        </button>
                      </div>

                      <AnimatePresence>
                        {isStepUp && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3, ease: easeInOut }}
                            className="mt-0  overflow-visible"
                          >
                            {/* Step-up Percentage */}
                            <div className=" rounded-xl border border-primaryBlue p-4 mb-4 flex mx-auto gap-4 bg-[#F4F8FF] shadow-[0px_0px_10px_0px_rgba(10,13,18,0.25)] xs:shadow-none">
                              <div className=" w-full">
                                <p className="text-sm xs:text-base mb-1 text-[#202020b5]">
                                  Step-up Amount
                                </p>

                                <div className="relative">
                                  <span className="absolute left-4 top-2.5 text-sm ">
                                    ₹
                                  </span>

                                  <input
                                    type="text"
                                    value={formatINR(stepUpPercent)}
                                    onChange={(e) => {
                                      let raw = e.target.value
                                        .replace(/,/g, "")
                                        .replace(/[^0-9]/g, "");
                                      if (!raw) raw = "0";

                                      const value = Number(raw);
                                      setStepUpPercent(raw);

                                      if (value < STEP_UP_MIN) {
                                        setStepUpError(
                                          "Step-up amount must be at least ₹1000",
                                        );
                                      } else if (value > STEP_UP_MAX) {
                                        setStepUpError(
                                          "Step-up amount should not be greater than ₹10,00,000",
                                        );
                                      } else if (value % 100 !== 0) {
                                        setStepUpError(
                                          "Step-up amount should be divisible by 100",
                                        );
                                      }
                                    }}
                                    placeholder=" e.g. 1000"
                                    className={`w-full rounded-xl pl-6 pr-4 py-2  mb-1 text-sm bg-white  border ${
                                      stepUpError
                                        ? "border-[#ec4d4d]"
                                        : "border-[#e4e4e4]"
                                    }
                                  focus:border-primaryBlue border border-[#e4e4e4] hover:border-[#a0a4ac]  transition duration-300`}
                                  />
                                </div>

                                {stepUpError && (
                                  <p className="text-2xs text-[#ec4d4d]">
                                    {stepUpError}
                                  </p>
                                )}
                              </div>

                              <div className="w-full relative group">
                                <p className="text-sm xs:text-base mb-1 text-[#202020b5]">
                                  Step-up Frequency
                                </p>

                                {/* Trigger */}
                                <button
                                  type="button"
                                  ref={periodButtonRef}
                                  onClick={() =>
                                    setIsDropdownOpenPeriod((prev) => !prev)
                                  }
                                  className={` py-2 w-full rounded-xl text-left pl-3  border border-[#e4e4e4]
                                            text-sm text-primaryBlack transition-all duration-200
                                            flex items-center justify-between mb-1 bg-white
                                            ${
                                              isDropdownOpenPeriod
                                                ? "border-[#a0a4ac]"
                                                : "border-[#e4e4e4] hover:border-[#a0a4ac]"
                                            }`}
                                >
                                  <span>{selectedPeriod.label}</span>

                                  <Icon.dropdownDownArrow
                                    className={`shrink-0 transition duration-300 mr-2
                                        ${
                                          isDropdownOpenPeriod
                                            ? "rotate-180 text-primaryBlue"
                                            : "text-gray-400 group-hover:text-primaryBlue"
                                        }`}
                                  />
                                </button>

                                {/* Dropdown */}
                                <CustomDropdown
                                  isOpen={isDropdownOpenPeriod}
                                  onClose={() => setIsDropdownOpenPeriod(false)}
                                  excludeRefs={[periodButtonRef]}
                                  className=" left-0 w-full rounded-xl shadow-md z-2  py-1"
                                  useHeight
                                >
                                  <div className="flex flex-col gap-1">
                                    {PERIODS.map((period) => (
                                      <button
                                        key={period.value}
                                        type="button"
                                        onClick={() => {
                                          setSelectedPeriod(period);
                                          setIsDropdownOpenPeriod(false);
                                        }}
                                        className={`py-2 rounded-lg text-sm transition 
            ${
              selectedPeriod.value === period.value
                ? "bg-primaryBlue text-white"
                : "hover:bg-gray-100 text-primaryBlack"
            }`}
                                      >
                                        {period.label}
                                      </button>
                                    ))}
                                  </div>
                                </CustomDropdown>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  )}

                  {/* Amount Input */}
                  <div className={investmentType === "SIP" ? "mb-4" : "mb-8"}>
                    <p className="text-sm xs:text-base mb-1.5 text-primaryBlack">
                      Enter Amount
                    </p>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-black text-sm">
                        ₹
                      </span>
                      <input
                        type="text"
                        value={amount}
                        onChange={handleAmountChange}
                        className={`border text-sm font-normal text-black w-full py-2.5 px-6 rounded-xl bg-white transition-all duration-200 ease-in-out ${showError ? "border-[#ec4d4d]" : "border-[#e4e4e4] hover:border-[#b6b5b5]"}`}
                      />
                    </div>
                    {showError && (
                      <p className="text-2xs text-[#ec4d4d]">{setError}</p>
                    )}
                    <div className="flex gap-2 mt-3">
                      {[1000, 3000, 5000].map((val) => (
                        <button
                          key={val}
                          onClick={(e) => handleQuickAmount(e, val)}
                          className="border border-[#e4e4e4] text-xs xs:text-base font-normal text-[#a1a1a1] px-3 py-1.5 xs:py-0.5 rounded-xl cursor-pointer hover:border-[#a7a7a7] hover:bg-[#f4f8ff] hover:text-[#666666] transition-all duration-300"
                        >
                          + ₹{val.toLocaleString()}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* SIP Date Selector */}
                  <AnimatePresence initial={false}>
                    {investmentType === "SIP" && (
                      <motion.div
                        key="sip-date"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto " }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: easeInOut }}
                        className={`mb-4 overflow-visible`}
                      >
                        <p className="text-sm xs:text-base mb-1.5 text-primaryBlack">
                          Select SIP Date
                        </p>

                        <div className="rounded-4xl p-1.75 bg-white border border-[#e4e4e4] grid gap-1 grid-cols-[repeat(auto-fit,minmax(40px,1fr))]">
                          {Array.from({ length: 28 }, (_, i) => i + 1).map(
                            (date) => (
                              <button
                                key={date}
                                onClick={(e) => {
                                  e.preventDefault();
                                  setSipDate(date);
                                }}
                                className={`text-sm w-10 h-10 py-2.5 px-2 cursor-pointer bg-white text-primaryBlack rounded-full transition-all duration-300 ${sipDate === date ? "border bg-[#f0f5fd] border-primaryBlue text-primaryBlue" : "hover:bg-[#f0f5fd]"}`}
                              >
                                {date}
                              </button>
                            ),
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Add to Cart Button */}
                  <button
                    disabled={isLoading || showError}
                    onClick={handleAddToCart}
                    className={`sticky bottom-7 xs:static cursor-pointer w-full text-white py-3 rounded-lg font-semibold shadow-[0px_2px_10px_8px_rgba(10,13,18,0.15)] xs:shadow-none transition-all duration-350 ease-in-out  ${showError ? "bg-primaryBlue opacity-80" : "bg-primaryBlue hover:bg-[#0e3c85] hover:scale-[1.01] hover:shadow-[0px_2px_10px_8px_rgba(10,13,18,0.15)]"}`}
                  >
                    {isLoading ? "Adding To Cart..." : "Add To Cart"}
                  </button>
                  {addError && (
                    <p className="text-2xs text-[#ec4d4d]">{addError}</p>
                  )}
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <motion.div
              className="text-center text-primaryBlack p-5 sm:p-6"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3, ease: easeInOut }}
            >
              <div className="w-full flex items-center justify-center mt-16 mb-2">
                <Lottie
                  animationData={successAnimation}
                  loop={false}
                  autoplay
                  className="w-28 h-28"
                />
              </div>
              <p className="text-xl sm:text-2xl font-bold mb-3">
                Fund Added Successfully!
              </p>
              <p className="text-base sm:text-lg mb-16">
                Your selected fund has been added. Complete your investment now.
              </p>
              <Link
                href="https://dashboard.mysiponline.com/cart"
                className="block w-full rounded-lg text-sm sm:text-base font-medium text-white py-2.5 mb-2.5 border border-primaryBlue bg-primaryBlue transition hover:bg-transparent hover:text-primaryBlue"
              >
                Go To Cart
              </Link>
              <button
                onClick={closeModal}
                className="block w-full rounded-lg text-sm sm:text-base font-medium text-[#414651] border border-[#cccccc] py-2.5 transition hover:bg-[#f4f8ff]"
              >
                Add More Funds
              </button>
            </motion.div>
          </>
        )}
      </CustomModal>
    </>
  );
}
